import { FC } from "react";

import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import { BasicWidgetHorizontal } from "src/dynamic-view-renderer/Widgets/BasicWidget/BasicWidgetHorizontal.tsx";
import { BasicWidgetVertical } from "src/dynamic-view-renderer/Widgets/BasicWidget/BasicWidgetVertical.tsx";
import { PropertyValueRendererParams } from "src/dynamic-view-renderer/Widgets/PropertyRenderer.tsx";
import { WidgetProperty } from "src/dynamic-view-renderer/Widgets/Widget.typings.ts";
import { WidgetContainerProps } from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { BlacklistedCaseIdentifier, CaseSummary } from "src/types/case";
import { ClientSchema } from "src/types/clientSchema";
import { Element } from "src/types/element";
import { PropertyValue } from "src/types/property";

import { useGetWidgetProperties } from "../WidgetProperties";

export type BasicWidgetProps = {
  data?: {
    //case , customer or blacklist
    item: {
      propertyValues?: PropertyValue[];
      elements?: Element[];
      blacklistedIdentifiers?: BlacklistedCaseIdentifier[];
      customProperties?: WidgetProperty[];
      source?: CaseSummary | null;
      code: string;
      id?: string;
      type: "case" | "customer" | "blacklist";
    };
    propertyValueRendererParams?: PropertyValueRendererParams;
  };

  itemSchema?: ClientSchema;
} & Omit<WidgetContainerProps, "children" | "itemId" | "topLabel" | "elementCode">;

const DEFAULT_ORIENTATION = "vertical";

const BasicWidget: FC<BasicWidgetProps> = ({ data, itemSchema, ...widgetContainerAttributes }) => {
  const uiSchema = useDynamicViewSchema();
  const getWidgetProperties = useGetWidgetProperties();

  const propertyDefinitions = uiSchema.propertyDefinitions;

  const widgetDefinition = widgetContainerAttributes.widgetDefinition;
  const propertyValueRendererParams = data?.propertyValueRendererParams;

  const orientation =
    widgetDefinition?.configuration?.orientation === "horizontal"
      ? "horizontal"
      : widgetDefinition?.configuration?.orientation === "vertical"
      ? "vertical"
      : DEFAULT_ORIENTATION;

  let widgetProperties = getWidgetProperties({
    itemSchema,
    widgetDefinition,
    data: data,
    propertyDefinitions,
  });

  // Add custom properties if they exist
  if (data?.item?.customProperties) {
    widgetProperties = [...data.item.customProperties, ...widgetProperties];
  }

  const basicWidgetAttributes = {
    properties: widgetProperties,
    propertyValueRendererParams,
    ...widgetContainerAttributes,
  };

  return orientation === "vertical" ? (
    <BasicWidgetVertical {...basicWidgetAttributes} />
  ) : (
    <BasicWidgetHorizontal {...basicWidgetAttributes} />
  );
};

export default BasicWidget;
